import type { OssArchiveDialogState } from "./oss-archive-error";

export interface OssArchiveDialogCopyItem {
	title: string;
	body: string;
	primary: string;
	secondary: string;
}

export interface OssArchiveToastCopy {
	applySuccess: string;
	applyMerged: string;
	applyFailed: string;
	refreshSuccess: string;
	refreshFailed: string;
	missingPid: string;
	missingPidInfo: string;
	refreshing: string;
}

export interface OssArchiveLocaleMessages {
	need: OssArchiveDialogCopyItem;
	pending: OssArchiveDialogCopyItem;
	ready: OssArchiveDialogCopyItem;
	expired: OssArchiveDialogCopyItem;
	failed: OssArchiveDialogCopyItem;
	forbidden: OssArchiveDialogCopyItem;
	degrade: OssArchiveDialogCopyItem;
	helpWhyTitle: string;
	helpWhyBody: string;
	whyLinkText: string;
	closeText: string;
	previewHint: string;
	toasts: OssArchiveToastCopy;
}

export type OssArchiveLocales = Record<string, OssArchiveLocaleMessages>;

export const OSS_ARCHIVE_DIALOG_STATES: OssArchiveDialogState[] = [
	"need",
	"pending",
	"ready",
	"expired",
	"failed",
	"forbidden",
	"degrade"
];

export const OSS_ARCHIVE_SUPPORTED_LOCALES = [
	{ value: "zh-CN", label: "简体中文" },
	{ value: "en", label: "English" },
	{ value: "zh-TW", label: "繁體中文" },
	{ value: "es", label: "Español" },
	{ value: "ko", label: "한국어" },
	{ value: "ja", label: "日本語" }
] as const;

export const OSS_ARCHIVE_DEFAULT_WAIT_TEXT: Record<string, string> = {
	"zh-CN": "12小时内",
	"zh-TW": "12小時內",
	en: "12 hours",
	es: "12 horas",
	ko: "12시간",
	ja: "12時間以内"
};

const ZH_CN: OssArchiveLocaleMessages = {
	need: {
		title: "数据已转入归档存储",
		body: "根据国家数据安全保护相关法律法规及平台数据留存策略，你访问的数据已转入归档存储，以持续保障数据安全与合规留存。访问前需完成一次恢复流程。提交申请后，预计约T可恢复访问。",
		primary: "申请恢复访问",
		secondary: "取消"
	},
	pending: {
		title: "恢复申请办理中",
		body: "你已授权，恢复正在办理中。请稍后再点击预览；若已解冻完成将直接打开，无需再次授权。预计约T可恢复访问。",
		primary: "",
		secondary: "知道了"
	},
	ready: {
		title: "已可重新获取",
		body: "恢复已完成，可直接预览。可访问时间有限，再次冻存后需重新授权。",
		primary: "立即预览",
		secondary: "关闭"
	},
	expired: {
		title: "恢复访问已过期",
		body: "上次恢复访问时限已结束，数据已重新转入归档存储。请重新授权后系统将再次自动办理。",
		primary: "重新授权恢复",
		secondary: "关闭"
	},
	failed: {
		title: "恢复申请未完成",
		body: "恢复申请未完成，请重新授权重试。如多次失败，请联系客服并提供订单号。",
		primary: "重新授权重试",
		secondary: "关闭"
	},
	forbidden: {
		title: "数据已转入归档存储",
		body: "当前账号无权申请恢复访问，请联系门店管理员或客服。",
		primary: "关闭",
		secondary: ""
	},
	degrade: {
		title: "数据已转入归档存储",
		body: "根据国家数据安全保护相关法律法规及平台数据留存策略，你访问的数据已转入归档存储。当前页面无法授权恢复，请前往门店后台办理。提交后预计约T可恢复访问。",
		primary: "",
		secondary: "关闭"
	},
	helpWhyTitle: "为什么需要申请?",
	helpWhyBody:
		"为落实《中华人民共和国数据安全法》数据分类分级保护要求，以及《网络数据安全管理条例》关于数据访问控制与留存的相关规定，平台对超过一定年限的历史数据实施归档存储与访问管控。解冻可能产生取回费用，因此每个冻存周期需你确认授权一次；之后请自行再次点击预览，可读时将直接打开。",
	whyLinkText: "为什么需要申请?",
	closeText: "关闭",
	previewHint: "该数据已转入归档存储，授权恢复后即可自动办理访问",
	toasts: {
		applySuccess: "已授权，恢复正在自动办理",
		applyMerged: "恢复正在办理中，无需重复授权",
		applyFailed: "授权失败，请稍后重试",
		refreshSuccess: "进度已更新",
		refreshFailed: "查询进度失败，请稍后重试",
		missingPid: "缺少 PID，无法提交恢复申请",
		missingPidInfo: "缺少 PID，无法查询进度",
		refreshing: "正在刷新…"
	}
};

const EN: OssArchiveLocaleMessages = {
	need: {
		title: "Data has been moved to archival storage",
		body: "Under applicable national data security laws and platform retention policies, the data you are accessing has been moved to archival storage to keep it secure and retained in compliance. A restore process is required before access. After you submit a request, it is typically available again in about T.",
		primary: "Apply for access restore",
		secondary: "Cancel"
	},
	pending: {
		title: "Restore in progress",
		body: "Restore was authorized and is processing. Click preview again later; once ready it opens directly with no re-authorization. Estimated wait: about T.",
		primary: "",
		secondary: "Got it"
	},
	ready: {
		title: "Ready to access",
		body: "Restore is complete. You can preview now. Access is time-limited; re-authorize after the next freeze.",
		primary: "Preview now",
		secondary: "Close"
	},
	expired: {
		title: "Restore access expired",
		body: "The previous restore window ended and data is archived again. Authorize once more to resume automatic processing.",
		primary: "Re-authorize restore",
		secondary: "Close"
	},
	failed: {
		title: "Restore request incomplete",
		body: "Restore could not be completed. Please re-authorize to retry. If it keeps failing, contact support with your order number.",
		primary: "Re-authorize and retry",
		secondary: "Close"
	},
	forbidden: {
		title: "Data has been moved to archival storage",
		body: "Your account cannot apply for restore access. Please contact your shop administrator or support.",
		primary: "Close",
		secondary: ""
	},
	degrade: {
		title: "Data has been moved to archival storage",
		body: "The data you are accessing has been moved to archival storage. This page cannot authorize restore. Please use the shop admin portal. After submission, access is typically restored within about T.",
		primary: "",
		secondary: "Close"
	},
	helpWhyTitle: "Why is an application needed?",
	helpWhyBody:
		"To comply with data classification and retention requirements, the platform archives older historical data and controls access. Restore may incur retrieval costs, so each freeze cycle requires one confirmation; then click preview again later, and it opens directly when ready.",
	whyLinkText: "Why is an application needed?",
	closeText: "Close",
	previewHint:
		"This data has been moved to archival storage. Access will be processed automatically after restore is authorized.",
	toasts: {
		applySuccess: "Authorized. Restore is processing automatically",
		applyMerged: "Restore is already in progress; no re-authorization needed",
		applyFailed: "Authorization failed. Please try again later",
		refreshSuccess: "Progress updated",
		refreshFailed: "Failed to refresh progress",
		missingPid: "PID is missing; cannot submit restore request",
		missingPidInfo: "PID is missing; cannot check progress",
		refreshing: "Refreshing…"
	}
};

const ZH_TW: OssArchiveLocaleMessages = {
	need: {
		title: "資料已轉入封存儲存",
		body: "根據國家資料安全保護相關法律法規及平台資料留存策略，你存取的資料已轉入封存儲存，以持續保障資料安全與合規留存。存取前需完成一次還原流程。提交申請後，預計約T可恢復存取。",
		primary: "申請還原存取",
		secondary: "取消"
	},
	pending: {
		title: "還原申請辦理中",
		body: "你已授權，還原正在辦理中。請稍後再點擊預覽；若已解凍完成將直接開啟，無需再次授權。預計約T可恢復存取。",
		primary: "",
		secondary: "知道了"
	},
	ready: {
		title: "已可重新取得",
		body: "還原已完成，可直接預覽。可存取時間有限，再次封存後需重新授權。",
		primary: "立即預覽",
		secondary: "關閉"
	},
	expired: {
		title: "還原存取已過期",
		body: "上次還原存取時限已結束，資料已重新轉入封存儲存。請重新授權後系統將再次自動辦理。",
		primary: "重新授權還原",
		secondary: "關閉"
	},
	failed: {
		title: "還原申請未完成",
		body: "還原申請未完成，請重新授權重試。如多次失敗，請聯絡客服並提供訂單號。",
		primary: "重新授權重試",
		secondary: "關閉"
	},
	forbidden: {
		title: "資料已轉入封存儲存",
		body: "目前帳號無權申請還原存取，請聯絡門市管理員或客服。",
		primary: "關閉",
		secondary: ""
	},
	degrade: {
		title: "資料已轉入封存儲存",
		body: "根據國家資料安全保護相關法律法規及平台資料留存策略，你存取的資料已轉入封存儲存。目前頁面無法授權還原，請前往門市後台辦理。提交後預計約T可恢復存取。",
		primary: "",
		secondary: "關閉"
	},
	helpWhyTitle: "為什麼需要申請?",
	helpWhyBody:
		"為落實資料分類分級保護與存取控制、留存相關規定，平台對超過一定年限的歷史資料實施封存儲存與存取管控。解凍可能產生取回費用，因此每個封存週期需你確認授權一次；之後請自行再次點擊預覽，可讀時將直接開啟。",
	whyLinkText: "為什麼需要申請?",
	closeText: "關閉",
	previewHint: "該資料已轉入封存儲存，授權還原後即可自動辦理存取",
	toasts: {
		applySuccess: "已授權，還原正在自動辦理",
		applyMerged: "還原正在辦理中，無需重複授權",
		applyFailed: "授權失敗，請稍後重試",
		refreshSuccess: "進度已更新",
		refreshFailed: "查詢進度失敗，請稍後重試",
		missingPid: "缺少 PID，無法提交還原申請",
		missingPidInfo: "缺少 PID，無法查詢進度",
		refreshing: "正在重新整理…"
	}
};

const ES: OssArchiveLocaleMessages = {
	need: {
		title: "Los datos se han movido a almacenamiento de archivo",
		body: "De acuerdo con las leyes de seguridad de datos y la política de retención de la plataforma, los datos a los que accede se han movido a almacenamiento de archivo para mantener la seguridad y la retención conforme. Debe completar un proceso de restauración antes de acceder. Tras enviar la solicitud, suele estar disponible de nuevo en unos T.",
		primary: "Solicitar restauración de acceso",
		secondary: "Cancelar"
	},
	pending: {
		title: "Restauración en curso",
		body: "La restauración está en proceso. Vuelva a pulsar previsualizar más tarde; cuando esté lista se abrirá directamente. Tiempo estimado: unos T.",
		primary: "",
		secondary: "Entendido"
	},
	ready: {
		title: "Listo para acceder",
		body: "La restauración ha finalizado. Ya puede previsualizar. El acceso es limitado; deberá autorizar de nuevo tras la próxima congelación.",
		primary: "Previsualizar ahora",
		secondary: "Cerrar"
	},
	expired: {
		title: "El acceso restaurado ha caducado",
		body: "La ventana de restauración anterior ha terminado y los datos vuelven a estar archivados. Autorice de nuevo para reanudar el proceso automático.",
		primary: "Volver a autorizar",
		secondary: "Cerrar"
	},
	failed: {
		title: "La solicitud de restauración no se completó",
		body: "No se pudo completar la restauración. Vuelva a autorizar para reintentar. Si sigue fallando, contacte con soporte e indique el número de pedido.",
		primary: "Reautorizar e intentar",
		secondary: "Cerrar"
	},
	forbidden: {
		title: "Los datos se han movido a almacenamiento de archivo",
		body: "Esta cuenta no puede solicitar la restauración de acceso. Contacte con el administrador de la tienda o con soporte.",
		primary: "Cerrar",
		secondary: ""
	},
	degrade: {
		title: "Los datos se han movido a almacenamiento de archivo",
		body: "Los datos a los que accede se han movido a almacenamiento de archivo. Esta página no puede autorizar la restauración. Hágalo en el portal de administración de la tienda. Tras enviarlo, el acceso suele restaurarse en unos T.",
		primary: "",
		secondary: "Cerrar"
	},
	helpWhyTitle: "¿Por qué se necesita una solicitud?",
	helpWhyBody:
		"Para cumplir los requisitos de clasificación y retención de datos, la plataforma archiva datos históricos antiguos y controla el acceso. La restauración puede generar costes de recuperación, por lo que cada ciclo de congelación requiere una confirmación; después vuelva a pulsar previsualizar y se abrirá cuando esté listo.",
	whyLinkText: "¿Por qué se necesita una solicitud?",
	closeText: "Cerrar",
	previewHint:
		"Estos datos se han movido a almacenamiento de archivo. El acceso se procesará automáticamente tras autorizar la restauración.",
	toasts: {
		applySuccess: "Autorizado. La restauración se está procesando",
		applyMerged: "La restauración ya está en curso; no es necesario volver a autorizar",
		applyFailed: "La autorización ha fallado. Inténtelo más tarde",
		refreshSuccess: "Progreso actualizado",
		refreshFailed: "No se pudo actualizar el progreso",
		missingPid: "Falta el PID; no se puede enviar la solicitud",
		missingPidInfo: "Falta el PID; no se puede consultar el progreso",
		refreshing: "Actualizando…"
	}
};

const KO: OssArchiveLocaleMessages = {
	need: {
		title: "데이터가 아카이브 스토리지로 이전되었습니다",
		body: "국가 데이터 보안 관련 법령 및 플랫폼 보관 정책에 따라, 접근하려는 데이터는 보안과 적법한 보관을 위해 아카이브 스토리지로 이전되었습니다. 접근 전에 복원 절차를 완료해야 합니다. 신청 제출 후 약 T 이면 다시 이용할 수 있습니다.",
		primary: "접근 복원 신청",
		secondary: "취소"
	},
	pending: {
		title: "복원 신청 처리 중",
		body: "복원이 처리 중입니다. 나중에 미리보기를 다시 클릭하세요. 해제가 끝나면 바로 열리며 재승인은 필요 없습니다. 예상 대기: 약 T.",
		primary: "",
		secondary: "확인"
	},
	ready: {
		title: "다시 이용할 수 있습니다",
		body: "복원이 완료되어 바로 미리볼 수 있습니다. 이용 가능 시간은 제한되며, 다시 동결되면 재승인이 필요합니다.",
		primary: "지금 미리보기",
		secondary: "닫기"
	},
	expired: {
		title: "복원 접근이 만료되었습니다",
		body: "이전 복원 기간이 끝나 데이터가 다시 아카이브되었습니다. 다시 승인하면 자동 처리가 재개됩니다.",
		primary: "다시 승인하여 복원",
		secondary: "닫기"
	},
	failed: {
		title: "복원 신청이 완료되지 않았습니다",
		body: "복원 신청이 완료되지 않았습니다. 다시 승인하여 재시도하세요. 반복 실패 시 주문번호를 알려 고객센터에 문의하세요.",
		primary: "다시 승인하고 재시도",
		secondary: "닫기"
	},
	forbidden: {
		title: "데이터가 아카이브 스토리지로 이전되었습니다",
		body: "현재 계정은 복원 접근을 신청할 권한이 없습니다. 매장 관리자 또는 고객센터에 문의하세요.",
		primary: "닫기",
		secondary: ""
	},
	degrade: {
		title: "데이터가 아카이브 스토리지로 이전되었습니다",
		body: "접근하려는 데이터는 아카이브 스토리지로 이전되었습니다. 이 페이지에서는 복원을 승인할 수 없습니다. 매장 관리 화면에서 진행하세요. 제출 후 약 T 이면 다시 이용할 수 있습니다.",
		primary: "",
		secondary: "닫기"
	},
	helpWhyTitle: "왜 신청이 필요한가요?",
	helpWhyBody:
		"데이터 분류·등급 보호와 접근 통제·보관 요건을 이행하기 위해, 플랫폼은 일정 연한이 지난 이력 데이터를 아카이브하고 접근을 통제합니다. 해제는 회수 비용이 발생할 수 있어 동결 주기마다 한 번 확인이 필요합니다. 이후 미리보기를 다시 클릭하면 준비되는 즉시 바로 열립니다.",
	whyLinkText: "왜 신청이 필요한가요?",
	closeText: "닫기",
	previewHint:
		"이 데이터는 아카이브 스토리지로 이전되었습니다. 복원을 승인하면 접근이 자동으로 처리됩니다.",
	toasts: {
		applySuccess: "승인되었습니다. 복원이 자동 처리 중입니다",
		applyMerged: "복원이 이미 진행 중입니다. 다시 승인할 필요가 없습니다",
		applyFailed: "승인에 실패했습니다. 나중에 다시 시도하세요",
		refreshSuccess: "진행 상태가 업데이트되었습니다",
		refreshFailed: "진행 상태 조회에 실패했습니다",
		missingPid: "PID가 없어 복원 신청을 제출할 수 없습니다",
		missingPidInfo: "PID가 없어 진행 상태를 조회할 수 없습니다",
		refreshing: "새로고침 중…"
	}
};

const JA: OssArchiveLocaleMessages = {
	need: {
		title: "データはアーカイブストレージへ移行されました",
		body: "関連するデータ保護法令およびプラットフォームの保管方針に基づき、アクセス先のデータは安全とコンプライアンス維持のためアーカイブストレージへ移行されています。アクセス前に復元手続きが必要です。申請後、およそ T で再取得できます。",
		primary: "アクセス復元を申請",
		secondary: "キャンセル"
	},
	pending: {
		title: "復元申請を処理中",
		body: "復元は処理中です。しばらくしてからプレビューを再度クリックしてください。解除が完了すればそのまま開き、再承認は不要です。目安は約 T です。",
		primary: "",
		secondary: "了解"
	},
	ready: {
		title: "再取得できます",
		body: "復元が完了し、すぐにプレビューできます。アクセス可能時間は限られ、再凍結後は再承認が必要です。",
		primary: "今すぐプレビュー",
		secondary: "閉じる"
	},
	expired: {
		title: "復元アクセスの期限が切れました",
		body: "前回の復元期間が終了し、データは再びアーカイブされています。再承認すると自動処理が再開されます。",
		primary: "再承認して復元",
		secondary: "閉じる"
	},
	failed: {
		title: "復元申請が完了していません",
		body: "復元申請を完了できませんでした。再承認して再試行してください。繰り返し失敗する場合は、注文番号を添えてサポートへご連絡ください。",
		primary: "再承認して再試行",
		secondary: "閉じる"
	},
	forbidden: {
		title: "データはアーカイブストレージへ移行されました",
		body: "このアカウントでは復元アクセスを申請できません。店舗管理者またはサポートにご連絡ください。",
		primary: "閉じる",
		secondary: ""
	},
	degrade: {
		title: "データはアーカイブストレージへ移行されました",
		body: "アクセス先のデータはアーカイブストレージへ移行されています。このページでは復元を承認できません。店舗管理画面で手続きしてください。提出後、およそ T で再取得できます。",
		primary: "",
		secondary: "閉じる"
	},
	helpWhyTitle: "なぜ申請が必要ですか?",
	helpWhyBody:
		"データの分類・等級保護およびアクセス制御・保管の要件に従い、プラットフォームは一定年数を超えた履歴データをアーカイブし、アクセスを管理しています。解除には取り出し費用が発生する場合があるため、凍結サイクルごとに一度の確認が必要です。その後、プレビューを再度クリックすると、準備でき次第そのまま開きます。",
	whyLinkText: "なぜ申請が必要ですか?",
	closeText: "閉じる",
	previewHint:
		"このデータはアーカイブストレージへ移行されています。復元を承認するとアクセスが自動処理されます。",
	toasts: {
		applySuccess: "承認しました。復元を自動処理しています",
		applyMerged: "復元はすでに進行中です。再承認は不要です",
		applyFailed: "承認に失敗しました。後でもう一度お試しください",
		refreshSuccess: "進捗を更新しました",
		refreshFailed: "進捗の取得に失敗しました",
		missingPid: "PID がないため復元申請を送信できません",
		missingPidInfo: "PID がないため進捗を確認できません",
		refreshing: "更新中…"
	}
};

const BUILTIN_BY_LOCALE: Record<string, OssArchiveLocaleMessages> = {
	"zh-CN": ZH_CN,
	en: EN,
	"zh-TW": ZH_TW,
	es: ES,
	ko: KO,
	ja: JA
};

export function createDefaultOssArchiveLocales(): OssArchiveLocales {
	return {
		"zh-CN": structuredClone(ZH_CN),
		en: structuredClone(EN),
		"zh-TW": structuredClone(ZH_TW),
		es: structuredClone(ES),
		ko: structuredClone(KO),
		ja: structuredClone(JA)
	};
}

export function createDefaultLocaleMessages(locale: string): OssArchiveLocaleMessages {
	return structuredClone(BUILTIN_BY_LOCALE[locale] || ZH_CN);
}

export const OSS_ARCHIVE_BUILTIN_DEFAULT_LOCALES = createDefaultOssArchiveLocales();

export function mergeOssArchiveLocales(
	remote?: OssArchiveLocales | null,
	fallback = OSS_ARCHIVE_BUILTIN_DEFAULT_LOCALES
): OssArchiveLocales {
	const out = structuredClone(fallback);
	if (!remote) {
		return out;
	}
	Object.entries(remote).forEach(([locale, messages]) => {
		if (!messages || typeof messages !== "object") {
			return;
		}
		out[locale] = {
			...(out[locale] || createDefaultLocaleMessages(locale)),
			...messages,
			toasts: {
				...(out[locale]?.toasts || createDefaultLocaleMessages(locale).toasts),
				...(messages.toasts || {})
			}
		};
	});
	return out;
}
